// ContactLimitBanner — aviso del tope de clientes del plan GRATIS (21-sep-2026).
//
// Por qué existe: el plan gratis admite hasta 1,000 clientes registrados (activos). Sin este
// aviso, el negocio se enteraría al chocar con el 403 al registrar — y en el kiosco el mensaje
// es neutro a propósito ("pregunta en caja"), porque lo lee la clienta. Aquí se le explica al
// dueño, con números, en cualquier pantalla: ámbar al 90 % y rojo al 100 %.
//
// Solo aparece si el backend manda `contactLimit.warn` (limit≠null = sin PRO vigente). Los PRO
// nunca lo ven; tampoco un gratis chico. Mismo sitio y patrón que PlanReadOnlyBanner.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const fmt = (n: number) => n.toLocaleString('es-MX');

const ContactLimitBanner: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const cl = currentUser?.contactLimit;
  if (!cl || cl.limit == null || !cl.warn) return null;

  const lleno = cl.blocked;
  const tone = lleno
    ? {
        box: 'border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10',
        icon: 'text-red-600 dark:text-red-400',
        title: 'text-red-900 dark:text-red-200',
        body: 'text-red-800 dark:text-red-300/90',
        btn: 'bg-red-600 hover:bg-red-700',
      }
    : {
        box: 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10',
        icon: 'text-amber-600 dark:text-amber-400',
        title: 'text-amber-900 dark:text-amber-200',
        body: 'text-amber-800 dark:text-amber-300/90',
        btn: 'bg-amber-600 hover:bg-amber-700',
      };
  const Icon = lleno ? AlertCircle : Users;

  return (
    <div role="status" className={`mb-4 rounded-lg border px-4 py-3 ${tone.box}`}>
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.icon}`} />
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${tone.title}`}>
            {lleno
              ? `Llegaste al límite de ${fmt(cl.limit)} clientes del plan gratis`
              : `Llevas ${fmt(cl.used)} de ${fmt(cl.limit)} clientes del plan gratis`}
          </p>
          <p className={`mt-0.5 text-sm ${tone.body}`}>
            {lleno
              ? 'Tus clientes actuales siguen acumulando y canjeando igual, pero ya no se registran nuevos. Con el plan PRO no hay límite.'
              : `Te quedan ${fmt(cl.remaining ?? 0)} registros. Al llegar al límite, los clientes que ya tienes siguen igual, pero no podrás registrar nuevos. Con el plan PRO no hay límite.`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/dashboard/subscription')}
          className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors ${tone.btn}`}
        >
          Ver plan PRO
        </button>
      </div>
    </div>
  );
};

export default ContactLimitBanner;
